import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Printer } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ViewWorksheet() {
  const navigate = useNavigate();
  const { worksheetId } = useParams();
  const [worksheet, setWorksheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAnswers, setShowAnswers] = useState(false);

  useEffect(() => {
    fetchWorksheet();
  }, [worksheetId]);

  const fetchWorksheet = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/assessments/worksheets/${worksheetId}`);
      setWorksheet(response.data);
    } catch (error) {
      toast.error('Failed to load worksheet');
      navigate('/assessments');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sky-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!worksheet) return null;

  return (
    <div className="min-h-screen bg-sky-50" data-testid="view-worksheet-page">
      <header className="glass-header py-4 px-6 print:hidden">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button onClick={() => navigate('/assessments')} variant="ghost" data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Assessments
          </Button>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowAnswers(!showAnswers)}
              variant="outline"
              className="rounded-full"
              data-testid="toggle-answers"
            >
              {showAnswers ? 'Hide Answers' : 'Show Answers'}
            </Button>
            <Button
              onClick={handlePrint}
              variant="outline"
              className="rounded-full"
              data-testid="print-button"
            >
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="glass-card p-8 print:shadow-none print:border-none">
          {/* Worksheet Header */}
          <div className="border-b border-slate-200 pb-6 mb-8">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <FileText className="w-8 h-8 text-purple-600" />
                  <h1 className="text-3xl font-bold text-slate-900">{worksheet.title}</h1>
                </div>
                <p className="text-slate-600">{worksheet.grade} • {worksheet.subject} • {worksheet.topic}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-purple-600">{worksheet.exercises?.length || 0}</p>
                <p className="text-sm text-slate-600">exercises</p>
              </div>
            </div>
          </div>

          {/* Student Info (for printing) */}
          <div className="hidden print:block mb-8 border-b border-slate-200 pb-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="border-b border-dotted border-slate-400 pb-2">
                <span className="text-sm text-slate-600">Name: </span>
              </div>
              <div className="border-b border-dotted border-slate-400 pb-2">
                <span className="text-sm text-slate-600">Date: </span>
              </div>
            </div>
          </div>

          {/* Exercises */}
          <div className="space-y-10">
            {worksheet.exercises?.map((exercise, index) => (
              <div key={index} className="border-b border-slate-100 pb-8 last:border-0">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold">
                    {index + 1}
                  </span>
                  <h3 className="text-lg font-semibold text-slate-900">Exercise {index + 1}</h3>
                </div>
                
                {/* Instruction */}
                {exercise.instruction && (
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-4">
                    <p className="text-purple-800 font-medium">{exercise.instruction}</p>
                  </div>
                )}

                {/* Content */}
                <div className="text-slate-700 whitespace-pre-wrap mb-4">
                  {exercise.content}
                </div>

                {/* Answer Space */}
                <div className="border border-slate-200 rounded-xl p-4 bg-white/50 min-h-[120px]">
                  <span className="text-sm text-slate-400">Your answer:</span>
                </div>

                {/* Show Answer Key */}
                {showAnswers && exercise.answer_key && (
                  <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4">
                    <span className="text-sm font-medium text-green-700 block mb-2">Answer Key:</span>
                    <p className="text-green-700 whitespace-pre-wrap">{exercise.answer_key}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
